import { Shield } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AddGrantDialog } from '@/components/admin/AddGrantDialog'
import { CreateLabelDialog } from '@/components/admin/CreateLabelDialog'
import { DeleteLabelDialog, RevokeGrantDialog } from '@/components/admin/LabelConfirmDialogs'
import { LabelGrantsPanel } from '@/components/admin/LabelGrantsPanel'
import { LabelList } from '@/components/admin/LabelList'
import { PageShell } from '@/components/layout/PageShell'
import { useLabelsAdmin } from '@/features/admin/hooks/useLabelsAdmin'
import { useRevealDetail } from '@/features/admin/hooks/useRevealDetail'
import type { DataLabel, GrantResponse } from '@/services/api/lake'

export default function AdminLabelsPage() {
  const { t } = useTranslation()
  const {
    labels,
    labelsLoading,
    selectedLabelId,
    setSelectedLabelId,
    selectedLabel,
    grants,
    grantsLoading,
    users,
    createLabel,
    deleteLabel,
    addGrant,
    updateGrant,
    revokeGrant,
  } = useLabelsAdmin()

  const [showCreateLabel, setShowCreateLabel] = useState(false)
  const [showAddGrant, setShowAddGrant] = useState(false)
  const [deletingLabel, setDeletingLabel] = useState<DataLabel | null>(null)
  const [revokingGrant, setRevokingGrant] = useState<GrantResponse | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const detail = useRevealDetail()

  return (
    <PageShell
      layout="padded"
      icon={Shield}
      title={t('admin.labels.title')}
      summary={t('admin.labels.summary')}
    >
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-2">
          <LabelList
            labels={labels}
            selectedLabelId={selectedLabelId}
            loading={labelsLoading}
            onSelect={id => {
              setSelectedLabelId(id)
              detail.reveal()
            }}
            onCreate={() => setShowCreateLabel(true)}
            onDelete={setDeletingLabel}
          />
        </div>
        <div ref={detail.ref} className="lg:col-span-3">
          <LabelGrantsPanel
            label={selectedLabel}
            grants={grants}
            grantsLoading={grantsLoading}
            onAddUser={() => setShowAddGrant(true)}
            onUpdatePermission={updateGrant}
            onRevoke={setRevokingGrant}
          />
        </div>
      </div>

      <CreateLabelDialog
        open={showCreateLabel}
        onOpenChange={setShowCreateLabel}
        submitting={submitting}
        onSubmit={async input => {
          setSubmitting(true)
          const ok = await createLabel(input)
          setSubmitting(false)
          if (ok) setShowCreateLabel(false)
        }}
      />

      <AddGrantDialog
        open={showAddGrant}
        onOpenChange={setShowAddGrant}
        labelName={selectedLabel?.name}
        users={users}
        existingGrants={grants}
        submitting={submitting}
        onSubmit={async (username, permission) => {
          setSubmitting(true)
          const ok = await addGrant(username, permission)
          setSubmitting(false)
          if (ok) setShowAddGrant(false)
        }}
      />

      <DeleteLabelDialog
        label={deletingLabel}
        onOpenChange={() => setDeletingLabel(null)}
        onConfirm={async () => {
          if (deletingLabel) await deleteLabel(deletingLabel)
          setDeletingLabel(null)
        }}
      />

      <RevokeGrantDialog
        grant={revokingGrant}
        labelName={selectedLabel?.name}
        onOpenChange={() => setRevokingGrant(null)}
        onConfirm={async () => {
          if (revokingGrant) await revokeGrant(revokingGrant)
          setRevokingGrant(null)
        }}
      />
    </PageShell>
  )
}
